import { ReactNode } from 'react';

interface Column<T> {
  key: string;
  label: string;
  align?: 'left' | 'right';
  render?: (row: T) => ReactNode;
}

interface RankTableCardProps<T> {
  title: string;
  subtitle?: string;
  rows: T[];
  columns: Column<T>[];
  emptyMessage?: string;
}

/**
 * Tabela simples dentro de um card. Usada quando comunicar várias métricas
 * por linha (inscritos, concluídos, taxa) é mais legível que um gráfico.
 */
function RankTableCard<T>({
  title,
  subtitle,
  rows,
  columns,
  emptyMessage = 'Sem dados no período',
}: RankTableCardProps<T>) {
  return (
    <div className="chart-card bg-surface-card border border-theme-border rounded-xl p-5 flex flex-col gap-3">
      <div>
        <h3 className="text-content-primary font-semibold">{title}</h3>
        {subtitle && <p className="text-content-muted text-xs">{subtitle}</p>}
      </div>
      {rows.length === 0 ? (
        <div className="text-content-muted text-sm py-6 text-center">{emptyMessage}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-content-muted text-xs uppercase tracking-wide">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`pb-2 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-t border-theme-border">
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`py-2 ${col.align === 'right' ? 'text-right' : 'text-left'} text-content-primary`}
                    >
                      {col.render
                        ? col.render(row)
                        : String((row as Record<string, unknown>)[col.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default RankTableCard;
